import type { LucideIcon } from "lucide-react";
import {
  MODULE_LUCIDE_FALLBACK,
  resolveModuleLucideIcon,
  type ModuleIconKey,
} from "components/ui/v2/sidebar/module-lucide-registry";
import { ICON_SIZES, type LucideIconSize } from "./lucide-icon-v2";
import { cn } from "@/lib/utils";

const NUMERIC_SIZE_CLASS: Record<number, string> = {
  12: ICON_SIZES.xs,
  14: ICON_SIZES.sm,
  16: ICON_SIZES.md,
  18: "h-[18px] w-[18px]",
  20: "h-5 w-5",
  24: ICON_SIZES.lg,
  28: "h-7 w-7",
  40: ICON_SIZES.xl,
};

function resolveSizeClass(size: LucideIconSize | number) {
  if (typeof size === "number") {
    return NUMERIC_SIZE_CLASS[size] ?? `h-[${size}px] w-[${size}px]`;
  }
  return ICON_SIZES[size];
}

export interface ModuleIconProps {
  /** Preferred: semantic module key (e.g. "explore") */
  readonly module?: ModuleIconKey | string;
  /** Legacy SVG path from ICONS_MODULES_PATHS / urlIcon */
  readonly path?: string;
  /** @deprecated Use path or module */
  readonly src?: string;
  readonly size?: LucideIconSize | number;
  readonly className?: string;
  readonly muted?: boolean;
  readonly onClick?: () => void;
  readonly "aria-label"?: string;
}

export function ModuleIcon({
  module,
  path,
  src,
  size = "md",
  className,
  muted = true,
  onClick,
  "aria-label": ariaLabel,
}: ModuleIconProps) {
  const resolved = module ?? path ?? src;
  const Icon: LucideIcon =
    resolveModuleLucideIcon(resolved) ?? MODULE_LUCIDE_FALLBACK;

  return (
    <Icon
      onClick={onClick}
      className={cn(
        "shrink-0 transition-all duration-200",
        resolveSizeClass(size),
        muted && "opacity-80",
        onClick && "cursor-pointer",
        className
      )}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
    />
  );
}

/** @deprecated Use ModuleIcon */
export const SidebarModuleIcon = ModuleIcon;
